import { createLogger } from "@/utils/logger"
import { computed, signal, useSignal } from "@preact/signals"
import { useCallback, useMemo, useRef, useEffect } from "preact/compat"
import styled from "@emotion/styled"
import Svg from "../base/Svg"
import { css } from "@emotion/react"
import { useModalSlideUp } from "@/features/useModal"
import { memo } from "preact/compat"
import dayjs from "dayjs"
import { hideScrollbarCss } from "@/styles/variables/css"
import { getCurrentPx } from "@/utils/common"
import { motion } from "motion/react"

const logger = createLogger("BaseDatePicker")

type Props = {
    date: Date
    onChange: (date: Date) => void
    formatDisplayValue: (date: Date) => React.ReactNode
    disabled?: boolean
    status: "idle" | "focus" | "error" | "valid" | "disabled"
    placeholder: string
    label: string
    constraints?: {
        min?: Date
        max?: Date
    }
}

const Container = styled.div`
    flex: 1;
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    font-family: "Gilroy", sans-serif;
    transition:
        box-shadow 0.2s ease-in-out,
        background-color 0.2s ease-in-out;
    flex: 1;
    min-height: 40rem;
    border: 1px solid #e0e0e0;
    border-radius: 8rem;
    padding: 0 16rem;
    cursor: pointer;
    > * {
        user-select: none;
        pointer-events: none;
    }
`

const DisplayValue = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex: 1;
    /* text */
    font-weight: 500;
    font-size: 16rem;
    line-height: 24rem;
    letter-spacing: 0%;
`

export function BaseDatePicker(props: Props) {
    const {
        //
        date: _date,
        onChange,
        disabled,
        status: _status,
        placeholder: _placeholder,
        label: _label,
        constraints,
    } = props
    const selectionId = useRef(`selection-${Math.random().toString(36).substring(2, 9)}`).current
    const {
        openWithId: openModal,
        close: closeModal,
        state: modalState,
    } = useModalSlideUp("selection")

    const initialDate = useRef(_date)
    const date = _date

    const finalStatus = useMemo(() => {
        if (disabled) {
            return "disabled"
        }

        return _status
    }, [_status, disabled])

    const finalBoxShadowColor = useMemo(() => {
        switch (finalStatus) {
            case "valid":
                return "#008000"
            case "error":
                return "#FF0000"
            case "focus":
                return "#0A6CFF"
            case "disabled":
                return "#c9c9c9"
            default:
                return "#e0e0e1"
        }
    }, [finalStatus])

    const finalBackgroundColor = useMemo(() => {
        switch (finalStatus) {
            case "valid":
                return "#e0ffe0"
            case "error":
                return "#ffe0e0"
            case "focus":
                return "#e6f0ff"
            case "disabled":
                return "#f0f0f0"
            default:
                return "#ffffff"
        }
    }, [finalStatus])

    const finalTextColor = useMemo(() => {
        switch (finalStatus) {
            case "valid":
                return "#008000"
            case "error":
                return "#ffadad"
            case "focus":
                return "#96c0ff"
            case "disabled":
                return "#c9c9c9"
            default:
                return "#000000"
        }
    }, [finalStatus])

    const finalIsModalOpen = useMemo(() => {
        return modalState.isOpen && selectionId === modalState.options?.activeId
    }, [modalState.isOpen, selectionId, modalState.options?.activeId])

    const handleClick = useCallback(() => {
        if (disabled) {
            return
        }
        openModal(
            <ModalDatePickerMemo
                date={date}
                label={_label}
                constraints={constraints}
                onConfirm={(props) => {
                    logger.info("props", props)
                    const _dayjs = dayjs()
                        .year(props.year)
                        .month(props.month - 1)
                        .date(props.day)
                    onChange(_dayjs.toDate())
                    closeModal()
                }}
                onCancel={() => {
                    closeModal()
                }}
            />,
            selectionId,
        )
    }, [_label, closeModal, date, disabled, onChange, openModal, selectionId, constraints])

    return (
        <Container
            css={css`
                box-shadow: inset 0 0 0 1.5rem ${finalBoxShadowColor};
                background-color: ${finalBackgroundColor};
                ${disabled ? "cursor: not-allowed;" : ""}
            `}
            onClick={handleClick}
        >
            <DisplayValue
                css={css`
                    color: ${finalTextColor};
                `}
            >
                {props.formatDisplayValue(date)}
            </DisplayValue>
            <Svg
                src="base_image/arrow_down.svg"
                css={css`
                    transition: transform 0.2s ease-in-out;
                    width: 24rem;
                    height: 24rem;
                    transform: rotate(${finalIsModalOpen ? "180deg" : "0deg"});
                    * {
                        fill: ${finalTextColor};
                    }
                `}
            />
        </Container>
    )
}

const ModalContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    max-height: 320rem;
    border-radius: 8rem 8rem 0 0;
    overflow: hidden;
    font-family: "Gilroy", sans-serif;
`

const HeaderContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 14rem 16rem;
    /* 下方 shadow */
    background-color: #ffffff;
    border-bottom: 2rem solid #d1d1d1;
`

const CancelButton = styled.div`
    font-family: Gilroy;
    font-weight: 700;
    font-size: 16rem;
    line-height: 24rem;
    letter-spacing: 0%;
    text-align: center;
    text-transform: capitalize;
    color: #ff356b;
    cursor: pointer;
`

const SelectLabel = styled.div`
    font-family: Gilroy;
    font-weight: 700;
    font-size: 16rem;
    line-height: 24rem;
    letter-spacing: 0%;
    text-align: center;
    text-transform: capitalize;
    user-select: none;
`

const ConfirmButton = styled.div`
    font-family: Gilroy;
    font-weight: 700;
    font-size: 16rem;
    line-height: 24rem;
    letter-spacing: 0%;
    text-align: center;
    text-transform: capitalize;
    color: #ff356b;
    cursor: pointer;
`

const ContentContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    justify-content: flex-start;
    height: 280rem;
    background-color: #ffffff;
    flex: 1;
    &::after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        top: 0;
        z-index: 1;
        user-select: none;
        pointer-events: none;

        background: linear-gradient(
            to bottom,
            #ffffff 0%,
            #ffffff70 40%,
            rgba(255, 255, 255, 0) 50%,
            #ffffff70 60%,
            #ffffff 100%
        );
    }
`

const OptionsColumn = styled.div`
    --padding-top: calc(140rem - 24rem);
    --padding-bottom: calc(140rem - 24rem);
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    flex: 1;
    overflow-y: scroll;
    padding-top: var(--padding-top);
    padding-bottom: var(--padding-bottom);
    ${hideScrollbarCss.styles}
`

const OptionItem = styled(motion.div)`
    min-height: 48rem;
    display: flex;
    align-items: center;
    justify-content: center;
    /* text */
    font-weight: 600;
    font-size: 16rem;
    line-height: 22rem;
    letter-spacing: 0%;
    text-align: center;
`

const normalizeNumber = (type: "year" | "month" | "day", value: number) => {
    if (type === "year") {
        // 展示 2025
        return dayjs().year(value).format("YYYY")
    }
    if (type === "month") {
        // 展示 文字 ex: August
        return dayjs()
            .month(value - 1)
            .format("MMMM")
    }
    if (type === "day") {
        return dayjs().date(value).format("D")
    }
}

function ModalDatePicker(props: {
    label: string
    date: Date
    constraints?: {
        min?: Date
        max?: Date
    }
    onCancel: () => void
    onConfirm: (props: { year: number; month: number; day: number }) => void
}) {
    const { label, date, onCancel, onConfirm, constraints } = props

    const minDate = computed(() => (constraints?.min ? dayjs(constraints.min) : null))
    const maxDate = computed(() => (constraints?.max ? dayjs(constraints.max) : null))

    const currentDayjs = useSignal(dayjs())

    useEffect(() => {
        currentDayjs.value = dayjs(date)
        return () => {
            currentDayjs.value = dayjs()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const currentYear = computed(() => currentDayjs.value.year())
    const currentMonth = computed(() => currentDayjs.value.month() + 1)
    const currentDay = computed(() => currentDayjs.value.date())

    const currentYearList = computed(() => {
        let years = Array.from({ length: 100 }, (_, i) => dayjs().subtract(i, "year").year())

        if (minDate) {
            const minYear = minDate.value?.year()
            if (minYear) {
                years = years.filter((year) => year >= minYear)
            }
        }

        if (maxDate) {
            const maxYear = maxDate.value?.year()
            if (maxYear) {
                years = years.filter((year) => year <= maxYear)
            }
        }

        return years
    })

    const currentMonthList = computed(() => {
        let months = Array.from(
            { length: 12 },
            (_, i) => dayjs().year(currentYear.value).month(i).month() + 1,
        )

        if (minDate && currentYear.value === minDate.value?.year()) {
            const minMonth = minDate.value?.month()
            if (minMonth) {
                months = months.filter((month) => month >= minMonth + 1)
            }
        }

        if (maxDate && currentYear.value === maxDate.value?.year()) {
            const maxMonth = maxDate.value?.month()
            if (maxMonth) {
                months = months.filter((month) => month <= maxMonth + 1)
            }
        }

        return months
    })

    const currentDayList = computed(() => {
        let days = Array.from({ length: currentDayjs.value.daysInMonth() }, (_, i) =>
            dayjs()
                .year(currentYear.value)
                .month(currentMonth.value - 1)
                .date(i + 1)
                .date(),
        )

        if (
            minDate &&
            currentYear.value === minDate.value?.year() &&
            currentMonth.value === minDate.value?.month() + 1
        ) {
            const minDay = minDate.value?.date()
            if (minDay) {
                days = days.filter((day) => day >= minDay)
            }
        }

        if (
            maxDate &&
            currentYear.value === maxDate.value?.year() &&
            currentMonth.value === maxDate.value?.month() + 1
        ) {
            const maxDay = maxDate.value?.date()
            if (maxDay) {
                days = days.filter((day) => day <= maxDay)
            }
        }

        return days
    })

    const options = computed(() => {
        return [currentMonthList.value, currentDayList.value, currentYearList.value]
    })

    const optionsColumnRefs = useRef<(HTMLDivElement | null)[]>([null, null, null])
    const isManualScrolling = useRef<boolean[]>([false, false, false])
    const scrollTimeouts = useRef<(NodeJS.Timeout | null)[]>([null, null, null])

    const scrollToSelectedPosition = useCallback(
        (
            scrollElement: HTMLElement,
            centerIndex: number,
            columnIndex: number,
            opts?: { timeout: number; behavior: "smooth" | "auto" },
        ) => {
            if (!scrollElement || isManualScrolling.current[columnIndex]) return

            if (scrollTimeouts.current[columnIndex]) {
                clearTimeout(scrollTimeouts.current[columnIndex]!)
            }

            const itemHeight = getCurrentPx(48)
            scrollTimeouts.current[columnIndex] = setTimeout(() => {
                scrollElement.scrollTo({
                    top: centerIndex * itemHeight,
                    behavior: opts?.behavior ?? "smooth",
                })
            }, opts?.timeout ?? 100)
        },
        [],
    )

    const handleScroll = useCallback(
        (type: "year" | "month" | "day", columnIndex: number, e: React.UIEvent<HTMLElement>) => {
            // 標記為手動滾動
            isManualScrolling.current[columnIndex] = true

            const target = e.target as HTMLElement
            const scrollTop = target.scrollTop
            const itemHeight = getCurrentPx(48)
            const centerIndex = Math.round(scrollTop / itemHeight)

            // 更新對應的日期值
            if (centerIndex >= 0) {
                switch (type) {
                    case "year":
                        if (centerIndex < currentYearList.value.length) {
                            currentDayjs.value = currentDayjs.value.set(
                                "year",
                                currentYearList.value[centerIndex],
                            )
                        }
                        break
                    case "month":
                        if (centerIndex < currentMonthList.value.length) {
                            currentDayjs.value = currentDayjs.value.set(
                                "month",
                                currentMonthList.value[centerIndex] - 1,
                            )
                        }
                        break
                    case "day":
                        if (centerIndex < currentDayList.value.length) {
                            currentDayjs.value = currentDayjs.value.set(
                                "date",
                                currentDayList.value[centerIndex],
                            )
                        }
                        break
                }
            }

            // 使用防抖清除手動滾動標記
            if (scrollTimeouts.current[columnIndex]) {
                clearTimeout(scrollTimeouts.current[columnIndex]!)
            }

            scrollTimeouts.current[columnIndex] = setTimeout(() => {
                // 滾動結束，重置手動滾動標記
                isManualScrolling.current[columnIndex] = false

                // 調整到最接近的有效位置
                scrollToSelectedPosition(target, centerIndex, columnIndex)
            }, 200)
        },
        [
            currentDayList.value,
            currentDayjs,
            currentMonthList.value,
            currentYearList.value,
            scrollToSelectedPosition,
        ],
    )

    const handleClickConfirm = computed(() => () => {
        logger.info("currentDayjs", currentDayjs.value.format("YYYY-MM-DD"))
        onConfirm({
            year: currentYear.value,
            month: currentMonth.value,
            day: currentDay.value,
        })
    })

    const currentYearIndex = computed(() => {
        return currentYearList.value.indexOf(currentYear.value)
    })

    const currentMonthIndex = computed(() => {
        return currentMonthList.value.indexOf(currentMonth.value)
    })

    const currentDayIndex = computed(() => {
        return currentDayList.value.indexOf(currentDay.value)
    })

    useEffect(() => {
        if (optionsColumnRefs.current.every((el) => el)) {
            const indices = [currentMonthIndex.value, currentDayIndex.value, currentYearIndex.value]
            const timeouts = [100, 100, 100]

            optionsColumnRefs.current.forEach((el, index) => {
                if (!isManualScrolling.current[index] && el && indices[index] >= 0) {
                    scrollToSelectedPosition(el, indices[index], index, {
                        timeout: timeouts[index],
                        behavior: "smooth",
                    })
                }
            })
        }
    }, [
        currentYearIndex.value,
        currentMonthIndex.value,
        currentDayIndex.value,
        scrollToSelectedPosition,
    ])

    return (
        <ModalContainer>
            <HeaderContainer>
                <CancelButton onClick={onCancel}>Cancel</CancelButton>
                <SelectLabel>{label ?? "Select"}</SelectLabel>
                <ConfirmButton onClick={handleClickConfirm.value}>Confirm</ConfirmButton>
            </HeaderContainer>
            <ContentContainer>
                {options.value.map((op, opIndex) => {
                    const type = ["month", "day", "year"] as const
                    return (
                        <OptionsColumn
                            ref={(el) => {
                                optionsColumnRefs.current[opIndex] = el
                            }}
                            onScroll={(e) => handleScroll(type[opIndex], opIndex, e)}
                        >
                            {op.map((num, numIndex) => {
                                return (
                                    <OptionItem
                                        key={`${num}-${numIndex}`}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{
                                            duration: 0.2,
                                            ease: "easeInOut",
                                        }}
                                    >
                                        {normalizeNumber(type[opIndex], num)}
                                    </OptionItem>
                                )
                            })}
                        </OptionsColumn>
                    )
                })}
            </ContentContainer>
        </ModalContainer>
    )
}

const ModalDatePickerMemo = memo(ModalDatePicker)

export default memo(BaseDatePicker)
